import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dealsAPI, notesAPI, filesAPI, usersAPI, organizationsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import UserAvatar from '../../components/UI/UserAvatar';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeftIcon, TrashIcon, PhoneIcon, EnvelopeIcon, BuildingOfficeIcon,
  DocumentArrowUpIcon, DocumentTextIcon, PlusIcon, CalendarIcon,
  CurrencyDollarIcon, ChartBarIcon, UserIcon, CheckCircleIcon,
  ChatBubbleLeftRightIcon, CheckIcon, XMarkIcon, PencilSquareIcon,
} from '@heroicons/react/24/outline';
import Modal from '../../components/UI/Modal';
import ConfirmDialog from '../../components/UI/ConfirmDialog';

const DEAL_STAGES = ['prospecting', 'qualification', 'proposal', 'negotiation', 'closed_won', 'closed_lost'];
const STAGE_LABELS = { prospecting: 'Prospecting', qualification: 'Qualification', proposal: 'Proposal', negotiation: 'Negotiation', closed_won: 'Closed Won', closed_lost: 'Closed Lost' };
const STAGE_COLORS = {
  prospecting: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800',
  qualification: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
  proposal: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800',
  negotiation: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800',
  closed_won: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800',
  closed_lost: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
};

const InlineEdit = ({ value, onSave, type = 'text', options, className = '', displayClassName = '', placeholder = 'Click to edit', renderDisplay }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);
  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const save = () => { if (draft !== value) onSave(draft); setEditing(false); };
  const cancel = () => { setDraft(value); setEditing(false); };
  const handleKeyDown = (e) => { if (e.key === 'Enter' && type !== 'textarea') save(); if (e.key === 'Escape') cancel(); };

  if (editing) {
    if (type === 'select' && options) {
      return (
        <select ref={inputRef} value={draft || ''} onChange={(e) => setDraft(e.target.value)} onBlur={() => setTimeout(save, 150)}
          className={`text-sm border border-primary-300 dark:border-primary-600 rounded-lg px-2 py-1 bg-white dark:bg-surface-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none ${className}`}>
          <option value="">-- Select --</option>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    if (type === 'textarea') {
      return (
        <div className="w-full">
          <textarea ref={inputRef} value={draft || ''} onChange={(e) => setDraft(e.target.value)} onKeyDown={handleKeyDown} rows={3}
            className={`w-full text-sm border border-primary-300 dark:border-primary-600 rounded-lg px-3 py-2 bg-white dark:bg-surface-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none resize-none ${className}`} placeholder={placeholder} />
          <div className="flex gap-1 mt-1">
            <button onClick={save} className="p-1 rounded bg-primary-500 text-white hover:bg-primary-600"><CheckIcon className="w-3.5 h-3.5" /></button>
            <button onClick={cancel} className="p-1 rounded bg-gray-200 dark:bg-surface-700 text-gray-600 dark:text-gray-300"><XMarkIcon className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      );
    }
    return (
      <input ref={inputRef} type={type} value={draft || ''} onChange={(e) => setDraft(e.target.value)} onKeyDown={handleKeyDown} onBlur={save}
        className={`text-sm border border-primary-300 dark:border-primary-600 rounded-lg px-2 py-1 bg-white dark:bg-surface-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none ${className}`} placeholder={placeholder} />
    );
  }
  return (
    <div onClick={() => setEditing(true)} className={`group/edit cursor-pointer rounded-lg px-2 py-1 -mx-2 -my-1 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors inline-flex items-center ${displayClassName}`}>
      {renderDisplay ? renderDisplay(value) : <span className={`${value ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500 italic'} text-sm`}>{value || placeholder}</span>}
      <PencilSquareIcon className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 opacity-0 group-hover/edit:opacity-100 transition-opacity flex-shrink-0 ml-1.5 -mr-5" />
    </div>
  );
};

const DealDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const { data: deal, isLoading } = useQuery({ queryKey: ['deal', id], queryFn: async () => { const r = await dealsAPI.getById(id); return r.data.data; } });
  const { data: users } = useQuery({ queryKey: ['users'], queryFn: async () => { const r = await usersAPI.getAll(); return r.data.data; } });
  const { data: organizations } = useQuery({ queryKey: ['organizations'], queryFn: async () => { const r = await organizationsAPI.getAll(); return r.data.data; } });
  const { data: notes } = useQuery({ queryKey: ['notes', 'deal', id], queryFn: async () => { const r = await notesAPI.getAll({ entityType: 'Deal', entityId: id }); return r.data.data; }, enabled: !!id });
  const { data: files } = useQuery({ queryKey: ['files', 'deal', id], queryFn: async () => { const r = await filesAPI.getAll({ entityType: 'Deal', entityId: id }); return r.data.data; }, enabled: !!id });

  const updateMutation = useMutation({
    mutationFn: (data) => dealsAPI.update(id, data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['deal', id] });
      if ('stage' in variables) {
        queryClient.invalidateQueries({ queryKey: ['deals'] });
      } else {
        queryClient.setQueriesData({ queryKey: ['deals', 'col'] }, (old) => {
          if (!old?.data) return old;
          return { ...old, data: old.data.map(d => d._id === id ? { ...d, ...variables } : d) };
        });
      }
      toast.success('Updated');
    },
    onError: (error) => toast.error(error.response?.data?.message || 'Failed to update'),
  });
  const deleteMutation = useMutation({ mutationFn: () => dealsAPI.delete(id), onSuccess: () => { toast.success('Deal deleted'); navigate('/deals'); } });
  const createNoteMutation = useMutation({
    mutationFn: (content) => notesAPI.create({ content, entityType: 'Deal', entityId: id }),
    onSuccess: () => { queryClient.invalidateQueries(['notes', 'deal', id]); toast.success('Note added'); setIsNoteModalOpen(false); setNoteContent(''); },
  });
  const deleteNoteMutation = useMutation({ mutationFn: (noteId) => notesAPI.delete(noteId), onSuccess: () => { queryClient.invalidateQueries(['notes', 'deal', id]); toast.success('Note deleted'); } });
  const deleteFileMutation = useMutation({ mutationFn: (fileId) => filesAPI.delete(fileId), onSuccess: () => { queryClient.invalidateQueries(['files', 'deal', id]); toast.success('File deleted'); } });

  const handleFileUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploadingFile(true);
    const fd = new FormData(); fd.append('file', file); fd.append('entityType', 'Deal'); fd.append('entityId', id);
    try { await filesAPI.upload(fd); queryClient.invalidateQueries(['files', 'deal', id]); toast.success('File uploaded'); }
    catch { toast.error('Failed to upload file'); }
    finally { setUploadingFile(false); e.target.value = ''; }
  };

  const handleFileDownload = async (file) => {
    try {
      const response = await filesAPI.download(file._id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download file'); }
  };

  const updateField = useCallback((field, value) => updateMutation.mutate({ [field]: value }), [updateMutation]);
  const handleDelete = () => setDeleteConfirmOpen(true);
  const confirmDelete = () => { deleteMutation.mutate(); setDeleteConfirmOpen(false); };

  const generateContract = async () => {
    try { await dealsAPI.generateContract(id); toast.success('Contract generated'); queryClient.invalidateQueries(['files', 'deal', id]); }
    catch { toast.error('Failed to generate contract'); }
  };

  const org = deal?.customerOrganization;
  const stageCfg = STAGE_COLORS[deal?.stage] || STAGE_COLORS.prospecting;

  if (isLoading) return (
    <div className="space-y-5">
      <div className="h-8 w-48 bg-gray-200 dark:bg-surface-700 rounded-lg animate-pulse" />
      <div className="bg-white dark:bg-surface-800 shadow-sm rounded-xl border border-gray-200 dark:border-surface-700 h-64 animate-pulse" />
    </div>
  );
  if (!deal) return <div className="text-center py-16"><p className="text-gray-500">Deal not found</p><Link to="/deals" className="btn btn-primary mt-4">Back to Deals</Link></div>;

  const currentStageIndex = DEAL_STAGES.indexOf(deal.stage);
  const progressStages = DEAL_STAGES.filter(s => s !== 'closed_lost');
  const tabs = [{ id: 'details', label: 'Details' }, { id: 'notes', label: 'Notes', count: notes?.length }, { id: 'files', label: 'Files', count: files?.length }];

  return (
    <motion.div className="space-y-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* Top Bar */}
      <div className="flex items-center justify-between">
        <Link to="/deals" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"><ArrowLeftIcon className="w-4 h-4" />Back to Deals</Link>
        <div className="flex items-center gap-2">
          <button onClick={generateContract} className="btn btn-success text-sm"><DocumentTextIcon className="w-4 h-4 mr-1.5" />Generate Contract</button>
          <button onClick={handleDelete} className="btn btn-ghost text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 p-2.5"><TrashIcon className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Header Card */}
      <div className="bg-white dark:bg-surface-800 rounded-2xl border border-gray-200 dark:border-surface-700 shadow-sm overflow-hidden">
        <div className="h-20 bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-400 relative">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMwLTkuOTQtOC4wNi0xOC0xOC0xOCIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMSkiIHN0cm9rZS13aWR0aD0iMiIvPjwvZz48L3N2Zz4=')] opacity-30" />
        </div>
        <div className="px-6 pb-5 -mt-8 relative z-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white shadow-lg border-4 border-white dark:border-surface-800">
              <CurrencyDollarIcon className="w-8 h-8" />
            </div>
            <div className="flex-1 min-w-0 sm:mb-0.5">
              <div className="flex flex-wrap items-center gap-3 mb-1">
                <span className="inline-flex px-2.5 py-0.5 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/30">
                  <InlineEdit value={deal.name || deal.title} onSave={(v) => updateField('name', v)} displayClassName="!text-xl !font-bold" placeholder="Deal name" />
                </span>
                <InlineEdit value={deal.stage} onSave={(v) => updateField('stage', v)} type="select" options={DEAL_STAGES.map(s => ({ value: s, label: STAGE_LABELS[s] }))}
                  renderDisplay={(v) => <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${stageCfg}`}>{STAGE_LABELS[v] || v}</span>} />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{deal.dealNumber}{org && ` · ${org.name}`}</p>
            </div>
          </div>
          {/* Key Metrics Row */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Value', value: deal.value ? `$${deal.value.toLocaleString()}` : 'N/A', icon: CurrencyDollarIcon, bg: 'bg-emerald-50 dark:bg-emerald-900/20', color: 'text-emerald-600 dark:text-emerald-400' },
              { label: 'Probability', value: `${deal.probability ?? 0}%`, icon: ChartBarIcon, bg: 'bg-blue-50 dark:bg-blue-900/20', color: 'text-blue-600 dark:text-blue-400' },
              { label: 'Weighted', value: `$${(((deal.value || 0) * (deal.probability || 0)) / 100).toLocaleString()}`, icon: ChartBarIcon, bg: 'bg-purple-50 dark:bg-purple-900/20', color: 'text-purple-600 dark:text-purple-400' },
              { label: 'Close Date', value: deal.expectedCloseDate ? new Date(deal.expectedCloseDate).toLocaleDateString() : 'Not set', icon: CalendarIcon, bg: 'bg-orange-50 dark:bg-orange-900/20', color: 'text-orange-600 dark:text-orange-400' },
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-xl p-3 text-center border border-gray-100 dark:border-surface-700`}>
                <s.icon className={`w-4 h-4 mx-auto mb-1 ${s.color}`} />
                <p className="text-xs text-gray-500 dark:text-gray-400">{s.label}</p>
                <p className={`text-sm font-bold ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stage Progress Bar */}
      <div className="bg-white dark:bg-surface-800 shadow-sm rounded-xl border border-gray-200 dark:border-surface-700 p-5">
        <div className="flex items-center justify-between relative">
          <div className="absolute top-5 left-8 right-8 h-0.5 bg-gray-200 dark:bg-gray-700" />
          <div className="absolute top-5 left-8 h-0.5 bg-primary-500 transition-all duration-500"
            style={{ width: deal.stage === 'closed_lost' ? '100%' : `${(currentStageIndex / (progressStages.length - 1)) * 100}%`, maxWidth: 'calc(100% - 4rem)' }} />
          {progressStages.map((stage, index) => {
            const isActive = deal.stage === 'closed_lost' ? false : index <= currentStageIndex;
            const isCurrent = stage === deal.stage;
            return (
              <button key={stage} className="flex flex-col items-center relative z-10" onClick={() => updateField('stage', stage)}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all cursor-pointer hover:scale-110 ${
                  isCurrent ? 'bg-primary-500 text-white ring-4 ring-primary-100 dark:ring-primary-900/30 shadow-lg'
                  : isActive ? 'bg-primary-500 text-white'
                  : 'bg-white dark:bg-surface-700 text-gray-400 border-2 border-gray-200 dark:border-gray-600'}`}>
                  {isActive ? <CheckCircleIcon className="w-5 h-5" /> : index + 1}
                </div>
                <p className={`mt-2 text-xs font-medium ${isCurrent ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`}>{STAGE_LABELS[stage]}</p>
              </button>
            );
          })}
        </div>
        {deal.stage === 'closed_lost' && (
          <div className="mt-3 text-center"><span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">Deal Lost{deal.lostReason ? `: ${deal.lostReason}` : ''}</span></div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-surface-700">
        <div className="flex gap-6">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}>
              {tab.label}{tab.count != null && <span className="ml-1.5 text-xs bg-gray-100 dark:bg-surface-700 text-gray-500 rounded-full px-1.5 py-0.5">{tab.count}</span>}
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'details' && (
          <motion.div key="details" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-5">
              {/* Deal Info - Editable */}
              <div className="bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700 shadow-sm">
                <div className="px-5 py-4 border-b border-gray-100 dark:border-surface-700">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2"><CurrencyDollarIcon className="w-4 h-4 text-primary-500" />Deal Information</h2>
                </div>
                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
                  <div><label className="text-xs text-gray-500 dark:text-gray-400 block mb-0.5">Value</label>
                    <InlineEdit value={deal.value} onSave={(v) => updateField('value', parseFloat(v) || 0)} type="number" placeholder="Deal value"
                      renderDisplay={(v) => <span className="text-sm font-semibold text-emerald-600">{v ? `$${Number(v).toLocaleString()}` : <span className="text-gray-400 italic font-normal">Add value</span>}</span>} /></div>
                  <div><label className="text-xs text-gray-500 dark:text-gray-400 block mb-0.5">Probability</label>
                    <InlineEdit value={deal.probability} onSave={(v) => updateField('probability', parseInt(v) || 0)} type="number" placeholder="%" renderDisplay={(v) => <span className="text-sm font-bold text-gray-900 dark:text-white">{v ?? 0}%</span>} /></div>
                  <div><label className="text-xs text-gray-500 dark:text-gray-400 block mb-0.5">Expected Close</label>
                    <InlineEdit value={deal.expectedCloseDate ? deal.expectedCloseDate.split('T')[0] : ''} onSave={(v) => updateField('expectedCloseDate', v)} type="date" placeholder="Set date" /></div>
                  <div><label className="text-xs text-gray-500 dark:text-gray-400 block mb-0.5">Organization</label>
                    <InlineEdit value={deal.customerOrganization?._id} onSave={(v) => updateField('customerOrganization', v)} type="select" options={organizations?.map(o => ({ value: o._id, label: o.name })) || []}
                      renderDisplay={() => <span className="text-sm">{org?.name || <span className="text-gray-400 italic">Select organization</span>}</span>} /></div>
                  <div><label className="text-xs text-gray-500 dark:text-gray-400 block mb-0.5">Assigned To</label>
                    <InlineEdit value={deal.assignedTo?._id} onSave={(v) => updateField('assignedTo', v)} type="select" options={users?.map(u => ({ value: u._id, label: `${u.firstName} ${u.lastName}` })) || []}
                      renderDisplay={() => deal.assignedTo ? <div className="flex items-center gap-2"><UserAvatar user={deal.assignedTo} size="sm" /><span className="text-sm font-medium">{deal.assignedTo.firstName} {deal.assignedTo.lastName}</span></div> : <span className="text-sm text-gray-400 italic">Unassigned</span>} /></div>
                </div>
              </div>

              {/* Contact Person - Editable */}
              <div className="bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700 shadow-sm">
                <div className="px-5 py-4 border-b border-gray-100 dark:border-surface-700">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2"><UserIcon className="w-4 h-4 text-primary-500" />Contact Person</h2>
                </div>
                <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-y-4 gap-x-8">
                  <div><label className="text-xs text-gray-500 dark:text-gray-400 block mb-0.5">Name</label>
                    <InlineEdit value={deal.customer?.name} onSave={(v) => updateField('customer', { ...deal.customer, name: v })} placeholder="Contact name" /></div>
                  <div><label className="text-xs text-gray-500 dark:text-gray-400 block mb-0.5">Email</label>
                    <InlineEdit value={deal.customer?.email} onSave={(v) => updateField('customer', { ...deal.customer, email: v })} type="email" placeholder="Contact email" /></div>
                  <div><label className="text-xs text-gray-500 dark:text-gray-400 block mb-0.5">Phone</label>
                    <InlineEdit value={deal.customer?.phone} onSave={(v) => updateField('customer', { ...deal.customer, phone: v })} type="tel" placeholder="Contact phone" /></div>
                </div>
              </div>

              {/* Description - Editable */}
              <div className="bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700 shadow-sm">
                <div className="px-5 py-4 border-b border-gray-100 dark:border-surface-700">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Description</h2>
                </div>
                <div className="p-5">
                  <InlineEdit value={deal.description} onSave={(v) => updateField('description', v)} type="textarea" placeholder="Add a description..." />
                </div>
              </div>

              {/* Organization Card */}
              {org && (
                <div className="bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700 shadow-sm">
                  <div className="px-5 py-4 border-b border-gray-100 dark:border-surface-700"><h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2"><BuildingOfficeIcon className="w-4 h-4 text-primary-500" />Organization</h2></div>
                  <div className="p-5 flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-100 to-primary-50 dark:from-primary-900/30 dark:to-primary-800/20 flex items-center justify-center flex-shrink-0"><BuildingOfficeIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" /></div>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">{org.name}</h3>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {org.industry && <div className="text-xs"><span className="text-gray-400">Industry:</span> <span className="text-gray-700 dark:text-gray-300 font-medium">{org.industry}</span></div>}
                        {org.email && <div className="text-xs"><span className="text-gray-400">Email:</span> <span className="font-medium">{org.email}</span></div>}
                        {org.phone && <div className="text-xs"><span className="text-gray-400">Phone:</span> <span className="font-medium">{org.phone}</span></div>}
                        {org.website && <div className="text-xs"><span className="text-gray-400">Website:</span> <a href={org.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 font-medium">{org.website}</a></div>}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-5">
              <div className="bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700 shadow-sm p-5 space-y-4">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Timeline</h3>
                {[
                  { label: 'Created', value: deal.createdAt },
                  { label: 'Updated', value: deal.updatedAt },
                  deal.expectedCloseDate && { label: 'Expected Close', value: deal.expectedCloseDate, accent: true },
                  deal.actualCloseDate && { label: 'Closed', value: deal.actualCloseDate },
                ].filter(Boolean).map(item => (
                  <div key={item.label} className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{item.label}</span>
                    <span className={`text-xs ${item.accent ? 'font-medium text-primary-600' : 'text-gray-700 dark:text-gray-300'}`}>{new Date(item.value).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
              {deal.lead && (
                <div className="bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700 shadow-sm p-5 space-y-2">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Related</h3>
                  <Link to={`/leads/${deal.lead._id || deal.lead}`} className="btn btn-secondary w-full justify-center text-sm rounded-xl">View Source Lead</Link>
                  {deal.quote && <Link to={`/quotes/${deal.quote._id || deal.quote}`} className="btn btn-secondary w-full justify-center text-sm rounded-xl">View Quote</Link>}
                </div>
              )}
              <div className="bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700 shadow-sm p-5 space-y-2.5">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Quick Actions</h3>
                {deal.customer?.email && <a href={`mailto:${deal.customer.email}`} className="btn btn-secondary w-full justify-center text-sm rounded-xl"><EnvelopeIcon className="w-4 h-4 mr-2" />Email Contact</a>}
                {deal.customer?.phone && <a href={`tel:${deal.customer.phone}`} className="btn btn-secondary w-full justify-center text-sm rounded-xl"><PhoneIcon className="w-4 h-4 mr-2" />Call</a>}
              </div>
              {/* Products */}
              {deal.products && deal.products.length > 0 && (
                <div className="bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700 shadow-sm">
                  <div className="px-5 py-4 border-b border-gray-100 dark:border-surface-700"><h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Products</h2></div>
                  <div className="p-5 space-y-2">
                    {deal.products.map((p, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 dark:border-gray-700 last:border-0">
                        <div><p className="text-sm font-medium text-gray-900 dark:text-white">{p.productName || p.product?.name}</p><p className="text-xs text-gray-400">Qty: {p.quantity}</p></div>
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">${p.total?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {activeTab === 'notes' && (
          <motion.div key="notes" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700 shadow-sm">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-surface-700 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Notes</h2>
              <button onClick={() => setIsNoteModalOpen(true)} className="btn btn-primary text-xs px-3 py-1.5"><PlusIcon className="w-3.5 h-3.5 mr-1" />Add Note</button>
            </div>
            <div className="p-5">{notes && notes.length > 0 ? (
              <div className="space-y-3">{notes.map(note => (
                <div key={note._id} className="bg-gray-50 dark:bg-surface-900 rounded-xl p-4 group hover:shadow-sm transition-all border border-transparent hover:border-gray-200 dark:hover:border-surface-600">
                  <div className="flex items-start justify-between"><p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap flex-1">{note.content}</p>
                    <button onClick={() => deleteNoteMutation.mutate(note._id)} className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity ml-3 flex-shrink-0"><TrashIcon className="w-4 h-4" /></button></div>
                  <div className="mt-2 flex items-center text-xs text-gray-400"><span>{note.createdBy?.firstName} {note.createdBy?.lastName}</span><span className="mx-1.5">·</span><span>{new Date(note.createdAt).toLocaleString()}</span></div>
                </div>
              ))}</div>
            ) : <div className="text-center py-12"><ChatBubbleLeftRightIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" /><p className="text-sm text-gray-400">No notes yet</p></div>}</div>
          </motion.div>
        )}

        {activeTab === 'files' && (
          <motion.div key="files" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700 shadow-sm">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-surface-700 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Files</h2>
              <label className="btn btn-primary text-xs px-3 py-1.5 cursor-pointer"><DocumentArrowUpIcon className="w-3.5 h-3.5 mr-1" />{uploadingFile ? 'Uploading...' : 'Upload'}<input type="file" className="hidden" onChange={handleFileUpload} disabled={uploadingFile} /></label>
            </div>
            <div className="p-5">{files && files.length > 0 ? (
              <div className="space-y-2">{files.map(file => (
                <div key={file._id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-surface-900 rounded-xl group hover:shadow-sm transition-all border border-transparent hover:border-gray-200 dark:hover:border-surface-600">
                  <div className="flex items-center min-w-0"><div className="w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center mr-3"><DocumentArrowUpIcon className="w-4 h-4 text-primary-500" /></div>
                    <div className="min-w-0"><button onClick={() => handleFileDownload(file)} className="text-sm font-medium text-primary-600 hover:text-primary-700 truncate block text-left">{file.originalName}</button><p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB · {new Date(file.uploadedAt || file.createdAt).toLocaleDateString()}</p></div></div>
                  <button onClick={() => deleteFileMutation.mutate(file._id)} className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity ml-3"><TrashIcon className="w-4 h-4" /></button>
                </div>
              ))}</div>
            ) : <div className="text-center py-12"><DocumentArrowUpIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" /><p className="text-sm text-gray-400">No files yet</p></div>}</div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Note Modal */}
      <Modal isOpen={isNoteModalOpen} onClose={() => setIsNoteModalOpen(false)} title="Add Note">
        <form onSubmit={(e) => { e.preventDefault(); createNoteMutation.mutate(noteContent); }} className="mt-4 space-y-4">
          <textarea required value={noteContent} onChange={(e) => setNoteContent(e.target.value)} rows={4} className="glass-input w-full" placeholder="Write your note..." />
          <div className="flex justify-end gap-3"><button type="button" onClick={() => setIsNoteModalOpen(false)} className="btn btn-secondary">Cancel</button><button type="submit" disabled={createNoteMutation.isPending} className="btn btn-primary">{createNoteMutation.isPending ? 'Adding...' : 'Add Note'}</button></div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Deal"
        message={`Are you sure you want to delete deal ${deal?.dealNumber}? This action cannot be undone.`}
        confirmText="Delete"
        type="danger"
      />
    </motion.div>
  );
};

export default DealDetail;






